import { supabase } from '@/integrations/supabase/client';
import { isDespiaIOS, isDespiaAndroid } from '@/hooks/use-despia';

const APPLE_CLIENT_ID = import.meta.env.VITE_APPLE_CLIENT_ID || '';
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const APP_URL = import.meta.env.VITE_APP_URL || window.location.origin;

// Platform detection
export function detectPlatform(): 'ios' | 'android' | 'web' {
  if (isDespiaIOS()) return 'ios';
  if (isDespiaAndroid()) return 'android';
  return 'web';
}

/**
 * Check if the current platform should show Apple Sign-In only
 * (i.e., Despia iOS native app)
 */
export function isAppleOnlyPlatform(): boolean {
  return isDespiaIOS();
}

/**
 * Get the redirect URI for Apple JS SDK — always use APP_URL
 */
function getRedirectURI(): string {
  try {
    return `${new URL(APP_URL).origin}/auth/apple/callback`;
  } catch {
    return `${window.location.origin}/auth/apple/callback`;
  }
}

/**
 * Initialize Apple JS SDK for iOS and Web — Android uses oauth:// flow
 */
export function initAppleAuth() {
  const platform = detectPlatform();

  // Android uses oauth:// protocol, no JS SDK needed
  if (platform === 'android') return;

  if (!APPLE_CLIENT_ID) {
    console.warn('[AppleAuth] VITE_APPLE_CLIENT_ID not set');
    return;
  }

  const redirectURI = getRedirectURI();

  if (typeof window !== 'undefined' && (window as any).AppleID) {
    (window as any).AppleID.auth.init({
      clientId: APPLE_CLIENT_ID,
      scope: 'name email',
      redirectURI,
      usePopup: true,
    });
    console.log('[AppleAuth] Initialized for', platform, 'with redirectURI:', redirectURI);
  } else {
    console.warn('[AppleAuth] AppleID JS SDK not loaded');
  }
}

/**
 * iOS/Web: Sign in using Apple JS SDK popup
 */
export async function signInWithAppleJS(): Promise<{
  idToken: string;
  code: string;
  user?: any;
}> {
  if (typeof window === 'undefined' || !(window as any).AppleID) {
    throw new Error('Apple JS SDK not loaded');
  }

  try {
    const response = await (window as any).AppleID.auth.signIn();
    return {
      idToken: response.authorization.id_token,
      code: response.authorization.code,
      user: response.user,
    };
  } catch (error: any) {
    if (error.error === 'popup_closed_by_user') {
      throw new Error('Sign in cancelled');
    }
    throw error;
  }
}

/**
 * Android ONLY: Sign in using oauth:// protocol (triggers ASWebAuthenticationSession)
 */
export function signInWithOAuthProtocol(deeplinkScheme: string = 'buxbox'): void {
  if (!APPLE_CLIENT_ID || !SUPABASE_URL) {
    throw new Error('Apple Sign In not configured');
  }

  const state = `${crypto.randomUUID()}|android|${deeplinkScheme}`;

  const params = new URLSearchParams({
    client_id: APPLE_CLIENT_ID,
    response_type: 'code id_token',
    response_mode: 'form_post',
    scope: 'name email',
    redirect_uri: `${SUPABASE_URL}/functions/v1/auth-apple-callback`,
    state: state,
  });

  const appleAuthUrl = `https://appleid.apple.com/auth/authorize?${params.toString()}`;

  // Use oauth:// protocol to trigger ASWebAuthenticationSession in Despia
  window.location.href = `oauth://?url=${encodeURIComponent(appleAuthUrl)}`;
}

/**
 * Set Supabase session from access + refresh tokens returned by edge function
 */
export async function setAppleSession(
  accessToken: string,
  refreshToken: string
): Promise<boolean> {
  try {
    const { error } = await supabase.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error) {
      console.error('[AppleAuth] setSession error:', error);
      return false;
    }
    return true;
  } catch (err) {
    console.error('[AppleAuth] setSession exception:', err);
    return false;
  }
}
